import { useState, useEffect, useRef } from 'react';
import SuperclusterManager, { MapLocation } from '../utils/SuperclusterManager';
import { sidebarStateManager, SidebarType } from '../utils/SidebarStateManager';

interface UseMarkerManagementProps {
  map: google.maps.Map | null;
  locations: MapLocation[];
  selectedMarkerId: string | null;
  setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | null>>;
  onRestaurantSelect?: (data: { id: string | number; location: google.maps.LatLngLiteral }) => void;
  onRestaurantDeselect: () => void;
}

export function useMarkerManagement({
  map,
  locations,
  selectedMarkerId,
  setSelectedMarkerId,
  onRestaurantSelect,
  onRestaurantDeselect,
}: UseMarkerManagementProps) {
  const [markerManager, setMarkerManager] = useState<SuperclusterManager | null>(null);
  const isSelectingNewMarker = useRef(false);
  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  // Initialize the marker manager — recreated whenever the map instance changes
  // (e.g. Google Maps recreates the map when colorScheme toggles)
  useEffect(() => {
    if (!map) return;

    const manager = new SuperclusterManager(map);

    manager.setMarkerClickHandler(async (locationId, position) => {
      isSelectingNewMarker.current = true;

      if (selectedMarkerId === locationId) {
        if (sidebarStateManager.getState().activeSidebar === SidebarType.RESTAURANT_DETAILS) {
          setSelectedMarkerId(null);
          onRestaurantDeselect();
        } else {
          sidebarStateManager.openSidebar(SidebarType.RESTAURANT_DETAILS);
        }
        isSelectingNewMarker.current = false;
        return;
      }

      setSelectedMarkerId(locationId);
      map.panTo({ lat: position.lat, lng: position.lng });

      if (onRestaurantSelect) {
        onRestaurantSelect({ id: locationId, location: position });
      }

      sidebarStateManager.openSidebar(SidebarType.RESTAURANT_DETAILS);
      setTimeout(() => { isSelectingNewMarker.current = false; }, 500);
    });

    setMarkerManager(manager);

    // Load existing locations into the new manager
    if (locationsRef.current.length > 0) {
      manager.updateLocations(locationsRef.current);
    }

    return () => {
      manager.destroy();
    };
  }, [map]); // Recreate when map instance changes

  // Update locations when they change
  useEffect(() => {
    if (!markerManager || !map) return;

    if (locations.length > 0) {
      markerManager.updateLocations(locations);
    }
  }, [map, locations, markerManager]);

  return { markerManager, isSelectingNewMarker } as const;
}
